import React, { useEffect, useContext, useState } from "react"
import PropTypes from "prop-types"
import { useParams } from "react-router-dom"
import i18n from "i18n"

import { Document, Page, pdfjs } from "react-pdf"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import Button from "@mui/material/Button"

import Budget2023Context from "context/budget2023/Budget2023Context"

const ModalPDFPreview = ({ open, onClose, onDownload, options }) => {
  const { pdfPreviewDownload } = useContext(Budget2023Context)
  const { projectId } = useParams()
  const [pdfUrl, setPdfUrl] = useState(null)
  const [numPages, setNumPages] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  // Guardar la url previa para cleanup
  const urlRef = React.useRef(null)

  useEffect(() => {
    let cancelled = false
    const fetchPreview = async () => {
      setLoading(true)
      setError(null)
      setNumPages(null)
      try {
        const previewOptions = {
          ...options,
          preview: true,
          preview_pages: 2,
        }
        const result = await pdfPreviewDownload(projectId, previewOptions)
        console.log("🚀 ~ fetchPreview ~ result:", result)

        const blobResult = result
        const isBlobLike =
          blobResult &&
          (blobResult instanceof Blob ||
            blobResult instanceof File ||
            (blobResult.constructor &&
              (blobResult.constructor.name === "Blob" ||
                blobResult.constructor.name === "File")))
        if (!cancelled && isBlobLike) {
          if (urlRef.current) URL.revokeObjectURL(urlRef.current)
          const newUrl = URL.createObjectURL(blobResult)
          urlRef.current = newUrl
          setPdfUrl(newUrl)
        } else {
          console.warn("El resultado no es un Blob/File o está cancelado", {
            cancelled,
            result,
            type: typeof result,
            constructor:
              result && result.constructor && result.constructor.name,
          })
        }
      } catch (e) {
        console.error("Error en fetchPreview:", e)
        if (!cancelled) setError("Error al cargar el PDF")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (open) fetchPreview()
    return () => {
      cancelled = true
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current)
        urlRef.current = null
      }
      setPdfUrl(null)
      setNumPages(null)
      setError(null)
    }
  }, [options, open, pdfPreviewDownload, projectId])

  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages)
  }

  function onDocumentLoadError(error) {
    setError("Error al renderizar el PDF")
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      width="500px"
      PaperProps={{ "data-testid": "pdf-preview-modal" }}
    >
      <DialogTitle>
        {i18n.t("pdfPreview.title")}
        <IconButton
          aria-label={i18n.t("Cerrar")}
          onClick={onClose}
          data-testid="pdf-preview-modal-close"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
        }}
      >
        <div
          style={{
            minHeight: 529,
            maxHeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {loading ? (
            <span>{i18n.t("pdfPreview.generatingPreview")}</span>
          ) : error ? (
            <span>{error}</span>
          ) : pdfUrl ? (
            <div
              style={{
                width: 480,
                height: 529,
                overflowY: "auto",
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                position: "relative",
              }}
            >
              <Document
                file={pdfUrl}
                loading=""
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
              >
                {numPages && numPages > 0
                  ? Array.from({ length: numPages }).map((_, idx) => (
                      <Page
                        key={idx}
                        pageNumber={idx + 1}
                        width={480}
                        loading=""
                      />
                    ))
                  : null}
              </Document>
            </div>
          ) : null}
        </div>
        <div style={{ padding: 16, width: 500, minWidth: 480, maxWidth: 500 }}>
          {i18n.t("pdfPreview.description")}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          variant="outlined"
          onClick={onClose}
          autoFocus
          data-testid="pdf-preview-close-button"
        >
          {i18n.t("Cerrar")}
        </Button>
        <Button
          color="branded"
          variant="contained"
          onClick={onDownload}
          autoFocus
          data-testid="pdf-preview-download-button"
        >
          {i18n.t("Descargar")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ModalPDFPreview.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  options: PropTypes.object.isRequired,
}

export default ModalPDFPreview
